import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Create axios instance with default config
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000, // Increased timeout to 30 seconds for slow connections
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor for logging
apiClient.interceptors.request.use(
    (config) => {
        console.log(`🚀 API Request: ${config.method?.toUpperCase()} ${config.url}`);
        return config;
    },
    (error) => {
        console.error('❌ API Request Error:', error);
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
    (response) => {
        console.log(`✅ API Response: ${response.status} ${response.config.url}`);
        return response;
    },
    (error) => {
        console.error('❌ API Response Error:', error);

        // Log the actual response data for debugging
        if (error.response?.data) {
            console.error('❌ Error response data:', error.response.data);
            if (error.response.data.details) {
                console.error('❌ Validation details:', JSON.stringify(error.response.data.details, null, 2));
            }
        }

        // Handle different error scenarios
        if (error.response) {
            // Server responded with error status
            const { status, data } = error.response;
            throw new Error(data.error || data.message || `API Error: ${status}`);
        } else if (error.request) {
            // Request was made but no response received
            throw new Error('Network error: Unable to reach server');
        } else {
            // Something else happened
            throw new Error(error.message || 'Unknown error occurred');
        }
    }
);

// API methods
export const api = {
    // Menu endpoints
    async getCategories() {
        const response = await apiClient.get('/menu/categories');
        return response.data;
    },

    async getItems() {
        const response = await apiClient.get('/menu/items');
        return response.data;
    },

    async getItemsByCategory(categoryId) {
        const response = await apiClient.get(`/menu/categories/${categoryId}/items`);
        return response.data;
    },

    async getItemModifiers(itemId) {
        const response = await apiClient.get(`/menu/items/${itemId}/modifiers`);
        return response.data;
    },

    // 🚀 NEW: Comprehensive endpoints for improved performance
    async getAllMenuData() {
        const response = await apiClient.get('/menu/all');
        return response.data;
    },

    async getModifierGroups() {
        const response = await apiClient.get('/menu/modifiers');
        return response.data;
    },

    // Order endpoint
    async createOrder(orderData) {
        // Serialize: strip any client-calculated monetary fields
        console.log('🔍 createOrder received orderData.items:', JSON.stringify(orderData.items, null, 2));

        const sanitized = {
            items: (orderData.items || []).map(it => {
                console.log('🔍 Processing item:', it.name, 'modifiers:', it.modifiers);
                return {
                    id: it.id,
                    name: it.name,
                    quantity: it.quantity,
                    modifiers: (it.modifiers || []).map(m => {
                        console.log('🔍 Modifier:', m.name, 'groupId:', m.groupId, 'groupName:', m.groupName);
                        return {
                            id: m.id,
                            groupId: m.groupId,      // ✅ Add groupId for exact matching
                            groupName: m.groupName,  // Keep for backward compatibility & logging
                            name: m.name,
                            quantity: m.quantity || 1
                        };
                    })
                };
            }),
            customer: orderData.customer,
            notes: orderData.notes,
            orderType: orderData.orderType, // Include order type (e.g., "Pickup", "Catering Order")
            pickupTime: orderData.pickupTime, // Include pickup time (ISO string or null for ASAP)
            taxExempt: orderData.taxExempt || false,
            // Lets the server decide whether to hold the kitchen ticket until the charge resolves.
            paymentMethod: orderData.paymentMethod || 'none'
        };
        const response = await apiClient.post('/order', sanitized);
        return response.data;
    },
    async previewOrder(orderData) {
        const sanitized = {
            items: (orderData.items || []).map(it => ({
                id: it.id,
                name: it.name,
                quantity: it.quantity,
                modifiers: (it.modifiers || []).map(m => ({
                    id: m.id,
                    groupId: m.groupId,      // ✅ Add groupId for exact matching
                    groupName: m.groupName,  // Keep for backward compatibility & logging
                    name: m.name,
                    quantity: m.quantity || 1
                }))
            })),
            customer: orderData.customer,
            notes: orderData.notes,
            orderType: orderData.orderType, // Include order type
            pickupTime: orderData.pickupTime, // Include pickup time
            taxExempt: orderData.taxExempt || false
        };
        const response = await apiClient.post('/order/preview', sanitized);
        return response.data;
    },

    // Merchant endpoints
    async getMerchantInfo() {
        const response = await apiClient.get('/menu/merchant');
        return response.data;
    },

    async checkItemAvailability(itemId) {
        const response = await apiClient.get(`/menu/items/${itemId}/availability`);
        return response.data;
    },

    async getPickupSlots(days = 1) {
        const response = await apiClient.get(`/menu/pickup-slots?days=${days}`);
        return response.data;
    },

    // Tax endpoints
    async getTaxRates(refresh = false) {
        const response = await apiClient.get(`/menu/taxes${refresh ? '?refresh=true' : ''}`);
        return response.data;
    },

    // Health check
    async healthCheck() {
        const response = await apiClient.get('/health');
        return response.data;
    }
    ,
    // Payment endpoints
    async getPaymentConfig() {
        const response = await apiClient.get('/payment/config');
        return response.data;
    },
    async initiatePayment({ orderId, cloverOrderId, amount, tipAmount = 0 }) {
        const response = await apiClient.post('/payment/initiate', { orderId, cloverOrderId, amount, tipAmount });
        return response.data;
    },
    async confirmPayment({ paymentRequestId, nonce, brand, last4 }) {
        // brand/last4 are display-only, for the "PAID ONLINE" banner on the kitchen ticket.
        const response = await apiClient.post('/payment/confirm', { paymentRequestId, nonce, brand, last4 });
        return response.data;
    },
    // Fallback when the charge never resolved (all retries exhausted). The SERVER picks the
    // banner wording; the client may only ask for the ticket, not dictate what it says.
    async printUnverified(orderId) {
        const response = await apiClient.post(`/order/${orderId}/print-unverified`, {});
        return response.data;
    }
};

export default api;
